import * as React from 'react';

import {
    Box,
    Divider,
    FormControlLabel,
    RadioGroup,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { createInvoiceIcon, createQuoteIcon, todoIcon } from '@europrocurement/l2d-icons';
import { FlexyButton, PriceLigne, Radio } from '@europrocurement/flexy-components';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import {
    CollectionDeDossierJsonldCollectiondossierRead,
    ReglementsEtAcomptesCBJsonldAcompteRead,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { DossiersArticlesReductions } from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierPrefacturation';
import { FactureJsonldFactureRead } from '@europrocurement/l2d-domain/openApi/ApiFacturation';
import { Container } from '@mui/system';
import { facturationType, heightFooterFacturation } from '../../functions';

type TotalsSectionProps = {
    collection: CollectionDeDossierJsonldCollectiondossierRead;
    totalAcomptes: number;
    acomptes: ReglementsEtAcomptesCBJsonldAcompteRead[];
    typeFacturation: facturationType;
    typesFacturation: {
        label: string;
        value: string;
        description: string;
    }[];
    dataPrefacturation: DossiersArticlesReductions;
    ajustInvoice: () => Promise<void>;
    afterSubmitInvoice: () => void;
    reloadInvoiceEstimates: () => void;
    updateFacturationType: (newTypeFacturation: facturationType) => Promise<void>;
    createEstimates: (
        idCollection: number,
    ) => Promise<CollectionDeDossierJsonldCollectiondossierRead>;
    createInvoice: (
        prefacture: DossiersArticlesReductions,
        acompteIds: Array<string>,
    ) => Promise<FactureJsonldFactureRead>;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setTypeFacturation: React.Dispatch<React.SetStateAction<facturationType>>;
};

const TotalsSection: React.FunctionComponent<TotalsSectionProps> = function ({
    collection,
    totalAcomptes,
    acomptes,
    typeFacturation,
    typesFacturation,
    dataPrefacturation,
    ajustInvoice,
    afterSubmitInvoice,
    reloadInvoiceEstimates,
    updateFacturationType,
    createEstimates,
    createInvoice,
    setLoading,
    setTypeFacturation,
}: TotalsSectionProps) {
    const { enqueueSnackbar } = useSnackbar();
    const { palette } = useTheme();

    return (
        <>
            <Box
                sx={{
                    background: palette.background.paper,
                    position: 'fixed',
                    boxShadow: '1px 1px 15px 0px rgba(0, 0, 0, 0.2)',
                    bottom: '0px',
                    left: '0px',
                    width: '100%',
                    height: heightFooterFacturation,
                }}
            />
            <Container
                sx={{
                    position: 'fixed',
                    bottom: '0px',
                    display: 'flex',
                    flexDirection: 'row',
                    height: heightFooterFacturation,
                    alignItems: 'center',
                }}
            >
                <Box>
                    <PriceLigne
                        label="Total HT"
                        price={`${dataPrefacturation.ht} €`}
                    />
                    <PriceLigne
                        labelTypographyProps={{ fontWeight: '600' }}
                        priceTypographyProps={{ fontWeight: '600' }}
                        label="Remises cumulées"
                        price={`${dataPrefacturation.remisesVisiblesCumulees?.ttc} €`}
                    />
                    <PriceLigne
                        label=" TVA"
                        price={`${dataPrefacturation.tva} €`}
                    />
                </Box>
                <Box>
                    <PriceLigne
                        label=" Total TTC"
                        price={`${dataPrefacturation.ttc} €`}
                    />
                    <PriceLigne
                        labelTypographyProps={{ fontWeight: '600' }}
                        priceTypographyProps={{ fontWeight: '600' }}
                        label="Provision"
                        price={`${totalAcomptes} €`}
                    />
                    <Divider />
                    <PriceLigne
                        labelTypographyProps={{ fontWeight: '600' }}
                        priceTypographyProps={{ fontSize: '22px', fontWeight: '600' }}
                        label="Montant à payer"
                        price={`${(parseFloat(dataPrefacturation.ttc ? dataPrefacturation.ttc : '0') - totalAcomptes).toFixed(2)} €`}
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'column',
                        gap: '5px',
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                    >
                        {typeFacturation && (
                            <RadioGroup
                                sx={{ flexGrow: '1' }}
                                aria-labelledby="invoice-type-radio-buttons-group-label"
                                color={palette.primary.dark}
                                name="invoice-type-buttons-group"
                                value={typeFacturation}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    const newValue = event.target.value;
                                    updateFacturationType(newValue as facturationType);
                                    setTypeFacturation(newValue as facturationType);
                                }}
                            >
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                >
                                    {typesFacturation.map(({ value, label, description }) => (
                                        <Tooltip title={description}>
                                            <FormControlLabel
                                                value={value}
                                                control={<Radio />}
                                                label={label}
                                            />
                                        </Tooltip>
                                    ))}
                                </Box>
                            </RadioGroup>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'row',
                            gap: '5px',
                        }}
                    >
                        <FlexyButton
                            variant="text"
                            color={palette.primary.dark}
                            disabled={
                                !(
                                    Math.abs(
                                        parseFloat(
                                            dataPrefacturation.ttc ? dataPrefacturation.ttc : '0',
                                        ) - totalAcomptes,
                                    ) <= 15
                                ) || !dataPrefacturation.facturable
                            }
                            onClick={() => {
                                setLoading(true);
                                ajustInvoice()
                                    .catch((error) => {
                                        setLoading(false);
                                        console.error('Une erreur est survenue:', error);
                                        if (
                                            error instanceof AxiosError &&
                                            error.response &&
                                            error.response.data.exception &&
                                            error.response.data.exception.message
                                        ) {
                                            enqueueSnackbar(
                                                <Typography>
                                                    {error.response.data.exception.message}
                                                </Typography>,
                                                {
                                                    variant: 'error',
                                                },
                                            );
                                        } else if (error instanceof Error) {
                                            enqueueSnackbar(
                                                <Typography>{error.message}</Typography>,
                                                {
                                                    variant: 'error',
                                                },
                                            );
                                        }
                                    })
                                    .then(() => {
                                        setLoading(false);
                                    });
                            }}
                            style={{ float: 'right', fontWeight: '500' }}
                            icon={todoIcon}
                            label="Ajuster le prix"
                        />
                        <FlexyButton
                            variant="outlined"
                            color={palette.primary.dark}
                            onClick={() => {
                                if (collection.id != null) {
                                    setLoading(true);
                                    createEstimates(collection.id)
                                        .then(() => {
                                            // Add notification to user.
                                            enqueueSnackbar('Le devis a été créé.', {
                                                variant: 'success',
                                            });
                                            setLoading(false);
                                            reloadInvoiceEstimates();

                                            const devisEtFacturesFormElement =
                                                document.getElementById('devisEtFactures');
                                            if (devisEtFacturesFormElement) {
                                                devisEtFacturesFormElement.scrollIntoView();
                                            }
                                        })
                                        .catch((error) => {
                                            setLoading(false);
                                            console.error('Une erreur est survenue:', error);
                                            if (
                                                error instanceof AxiosError &&
                                                error.response &&
                                                error.response.data.exception &&
                                                error.response.data.exception.message
                                            ) {
                                                enqueueSnackbar(
                                                    <Typography>
                                                        {error.response.data.exception.message}
                                                    </Typography>,
                                                    {
                                                        variant: 'error',
                                                    },
                                                );
                                            } else if (error instanceof Error) {
                                                enqueueSnackbar(
                                                    <Typography>{error.message}</Typography>,
                                                    {
                                                        variant: 'error',
                                                    },
                                                );
                                            }
                                        });
                                }
                            }}
                            icon={createQuoteIcon}
                            label="Créer le devis"
                        />
                        <FlexyButton
                            disabled={!dataPrefacturation.facturable}
                            variant="contained"
                            color={palette.primary.dark}
                            onClick={() => {
                                setLoading(true);
                                const acompteIds: string[] = acomptes.flatMap((acompte) =>
                                    (acompte.detailsReglements ?? []).map((detail) => {
                                        if (detail.id === undefined || detail.id === null) {
                                            throw new Error('detail sans id');
                                        }
                                        return detail.id as unknown as string;
                                    }),
                                );
                                createInvoice(dataPrefacturation, acompteIds)
                                    .catch((error) => {
                                        setLoading(false);
                                        console.error('Une erreur est survenue:', error);
                                        if (
                                            error instanceof AxiosError &&
                                            error.response &&
                                            error.response.data.exception &&
                                            error.response.data.exception.message
                                        ) {
                                            enqueueSnackbar(
                                                <Typography>
                                                    {error.response.data.exception.message}
                                                </Typography>,
                                                {
                                                    variant: 'error',
                                                },
                                            );
                                        } else if (error instanceof Error) {
                                            enqueueSnackbar(
                                                <Typography>{error.message}</Typography>,
                                                {
                                                    variant: 'error',
                                                },
                                            );
                                        }
                                    })
                                    .then(() => {
                                        afterSubmitInvoice();
                                        // Add notification to user.
                                        setLoading(false);
                                        enqueueSnackbar('La facture a été créée.', {
                                            variant: 'success',
                                        });
                                        reloadInvoiceEstimates();

                                        const devisEtFacturesFormElement =
                                            document.getElementById('devisEtFactures');
                                        if (devisEtFacturesFormElement) {
                                            devisEtFacturesFormElement.scrollIntoView();
                                        }
                                    });
                            }}
                            icon={createInvoiceIcon}
                            label="Créer la facture"
                        />
                    </Box>
                </Box>
            </Container>
        </>
    );
};

export default TotalsSection;
